import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import SingleSelectDropdownFilter from 'components/SingleSelectDropdownFilter';
import { TIMEFRAME } from 'dateUtils';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import useQueryParams from 'hooks/useQueryParams';
import { PAGE_PARAM_KEYWORD, TIMEFRAME_PARAM_KEYWORD } from 'data/global';
import { NAVIGATION_VARIANTS } from 'models/Navigation';
import { Option } from 'models/Global';
import { getResetedPageParams } from 'utils';

import './TimeframeDropdownFilter.styles.scss';

type TimeframeDropdownFilterProps = {
  className?: string;
  timeframeOptions?: Option[];
  title: string;
  pageParams?: string[];
};

const TimeframeDropdownFilter: React.FC<TimeframeDropdownFilterProps> = (
  props,
) => {
  const {
    className,
    timeframeOptions,
    title,
    pageParams = [PAGE_PARAM_KEYWORD],
  } = props;

  const classes = classNames('timeframe-dropdown-filter', className);

  const { t } = useTranslation();
  const createSelectOptions = useCreateSelectOptions();

  const { params, setMultipleQueryParams } = useQueryParams();

  const options = useMemo(
    () => timeframeOptions || createSelectOptions(Object.values(TIMEFRAME)),
    [timeframeOptions, createSelectOptions],
  );

  const selectedTimeframe = useMemo(
    () => params[TIMEFRAME_PARAM_KEYWORD],
    [params],
  );

  const triggerTitle = useMemo(
    () => (selectedTimeframe ? t(selectedTimeframe) : title),
    [selectedTimeframe, title, t],
  );

  const handleChange = useCallback(
    (nextValue: string) => {
      const resetedPageParams = getResetedPageParams(pageParams);

      setMultipleQueryParams({
        ...resetedPageParams,
        [TIMEFRAME_PARAM_KEYWORD]: nextValue,
      });
    },
    [pageParams, setMultipleQueryParams],
  );

  return (
    <SingleSelectDropdownFilter
      className={classes}
      variant={NAVIGATION_VARIANTS.SECONDARY}
      selectedValue={selectedTimeframe}
      triggerTitle={triggerTitle}
      options={options}
      onChange={handleChange}
    />
  );
};

export default TimeframeDropdownFilter;
